import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import './ClassificationItemModal.scss';

interface ClassificationConfirmDialogProps {
  titleKey: string;
  messageKey: string;
  messageParams?: Record<string, string>;
  confirmKey: string;
  cancelKey: string;
  titleId: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ClassificationConfirmDialog: React.FC<ClassificationConfirmDialogProps> = ({
  titleKey,
  messageKey,
  messageParams,
  confirmKey,
  cancelKey,
  titleId,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pendingActionRef = useRef(false);

  const handleCancel = useCallback(() => {
    if (!pendingActionRef.current) onCancel();
  }, [onCancel]);

  const handleConfirm = () => {
    if (pendingActionRef.current) return;
    pendingActionRef.current = true;
    onConfirm();
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) {
      try { dialog.showModal(); } catch { dialog.setAttribute('open', ''); }
    }
    const handleClick = (e: MouseEvent) => { if (e.target === dialog) handleCancel(); };
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') handleCancel(); };
    dialog.addEventListener('click', handleClick);
    dialog.addEventListener('keydown', handleKeyDown);
    return () => {
      dialog.removeEventListener('click', handleClick);
      dialog.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCancel]);

  return (
    <dialog
      ref={dialogRef}
      className="classification-tag-confirm__overlay"
      onClose={handleCancel}
      aria-labelledby={titleId}
    >
      <div className="classification-tag-confirm">
        <div className="classification-tag-confirm__header">
          <BdsIcon theme="outline" name="warning" size="medium" />
          <span id={titleId} className="classification-tag-confirm__title">
            {t(titleKey)}
          </span>
        </div>
        <div className="classification-tag-confirm__body">
          <p className="classification-tag-confirm__text">
            {t(messageKey, messageParams)}
          </p>
          <div className="classification-tag-confirm__buttons">
            <button
              className="classification-tag-confirm__btn classification-tag-confirm__btn--secondary"
              onClick={handleCancel}
              title={t(cancelKey)}
            >
              {t(cancelKey)}
            </button>
            <button
              className="classification-tag-confirm__btn classification-tag-confirm__btn--primary"
              onClick={handleConfirm}
              title={t(confirmKey)}
            >
              {t(confirmKey)}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
};
